import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  ApiFailure,
  type Aggregation,
  type CalendarPeriod,
  type Column,
  type Dataset,
  type DashboardSpec,
  type Filter,

  type ChartKind,
  type PivotTable,
  type Report,
} from '../api';
import type { VizColors } from '../theme';
import { CategoryChart, ScatterPlot, ShareChart, TrendChart } from './Charts';
import { Notice } from './Panels';

const AGGREGATIONS: Aggregation[] = ['sum', 'avg', 'count', 'countDistinct', 'min', 'max', 'median'];
const PERIODS: CalendarPeriod[] = ['day', 'week', 'month', 'quarter', 'year'];

function useAsync<T>(run: () => Promise<T>, deps: unknown[]): {
  data: T | null;
  failure: ApiFailure | null;
  busy: boolean;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setFailure(null);

    run()
      .then((value) => {
        if (!cancelled) setData(value);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // A failed query must not leave the previous result on screen looking
        // like an answer to the new question.
        setData(null);
        setFailure(
          error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0),
        );
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, failure, busy, reload: useCallback(() => setNonce((n) => n + 1), []) };
}

function measures(columns: Column[]): Column[] {
  return columns.filter((c) => c.type === 'numeric' && !c.isIdentifier);
}

function dimensions(columns: Column[]): Column[] {
  return columns.filter((c) => c.type === 'categorical' && !c.isIdentifier);
}

function temporals(columns: Column[]): Column[] {
  return columns.filter((c) => c.type === 'temporal');
}

function num(value: number | null): string {
  return value === null ? '' : value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

// -----------------------------------------------------------------------------
// "View these records" — drill from an aggregate to the rows behind it
// -----------------------------------------------------------------------------

/** A bucket the user clicked, expressed as the filters that select its rows. */
interface DrillSelection {
  label: string;
  filters: Filter[];
}

/**
 * The aggregation layers label a missing group key "(blank)". Equality would
 * match nothing — a missing value participates in no comparison — so the blank
 * bucket drills down with `isNull` instead.
 */
function cellFilter(column: string, key: string): Filter {
  return key === '(blank)'
    ? { column, operator: 'isNull' }
    : { column, operator: 'equals', value: key };
}

/**
 * Turn a calendar bucket key ("2024-03", "2024-Q1", "2024-W05", …) back into an
 * inclusive date range. This mirrors `truncateToPeriod`/`periodStart` in the
 * analytics core; the two must agree or a bucket would drill into the wrong rows.
 */
function periodRange(key: string, period: CalendarPeriod): [string, string] | null {
  const DAY = 86_400_000;

  const quarter = /^(\d{4})-Q([1-4])$/.exec(key);
  if (quarter !== null) {
    const start = Date.UTC(Number(quarter[1]), (Number(quarter[2]) - 1) * 3, 1);
    const end = Date.UTC(Number(quarter[1]), (Number(quarter[2]) - 1) * 3 + 3, 1) - 1;
    return [new Date(start).toISOString(), new Date(end).toISOString()];
  }

  const week = /^(\d{4})-W(\d{2})$/.exec(key);
  if (week !== null) {
    // ISO week: the Monday of the week containing the year's first Thursday.
    const fourth = new Date(Date.UTC(Number(week[1]), 0, 4));
    const monday = new Date(fourth);
    monday.setUTCDate(fourth.getUTCDate() - ((fourth.getUTCDay() + 6) % 7));
    monday.setUTCDate(monday.getUTCDate() + (Number(week[2]) - 1) * 7);
    const end = monday.getTime() + 7 * DAY - 1;
    return [monday.toISOString(), new Date(end).toISOString()];
  }

  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (day !== null) {
    const start = Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
    return [new Date(start).toISOString(), new Date(start + DAY - 1).toISOString()];
  }

  const month = /^(\d{4})-(\d{2})$/.exec(key);
  if (month !== null) {
    const start = Date.UTC(Number(month[1]), Number(month[2]) - 1, 1);
    const end = Date.UTC(Number(month[1]), Number(month[2]), 1) - 1;
    return [new Date(start).toISOString(), new Date(end).toISOString()];
  }

  const year = /^(\d{4})$/.exec(key);
  if (year !== null) {
    const start = Date.UTC(Number(year[1]), 0, 1);
    const end = Date.UTC(Number(year[1]) + 1, 0, 1) - 1;
    return [new Date(start).toISOString(), new Date(end).toISOString()];
  }

  return null;
}

/** The filters selecting the rows behind one aggregate bucket. */
function drillFilters(
  groupBy: string,
  isTemporal: boolean,
  period: CalendarPeriod,
  key: string,
): Filter[] {
  if (isTemporal) {
    const range = periodRange(key, period);
    return range === null ? [] : [{ column: groupBy, operator: 'between', value: range }];
  }
  return [cellFilter(groupBy, key)];
}

/** How many rows the drill-down table shows. The full count is always stated. */
const RECORDS_PAGE_SIZE = 100;

/**
 * The rows behind an aggregate, fetched through the render endpoint with a
 * table-only spec.
 *
 * `GET /datasets/:id/rows` pages rows but cannot filter them, and re-downloading
 * the whole dataset to filter in the browser is exactly what that cap exists to
 * prevent. The render endpoint already executes a spec against one filtered
 * view of the rows, so a spec whose only block is a table returns the filtered
 * records in one call.
 */
function RecordsPanel({
  dataset,
  drill,
  onClose,
}: {
  dataset: Dataset;
  drill: DrillSelection;
  onClose: () => void;
}) {
  const spec = useMemo<DashboardSpec>(
    () => ({
      version: 1,
      title: drill.label,
      roles: {
        primaryMeasure: null,
        measures: [],
        primaryDimension: null,
        dimensions: [],
        dateColumn: null,
        excluded: [],
      },
      blocks: [
        {
          id: 'records',
          kind: 'table',
          title: drill.label,
          subtitle: '',
          rationale: '',
          width: 'full',
          score: 0,
          columns: dataset.columns.map((c) => c.name),
          pageSize: RECORDS_PAGE_SIZE,
        },
      ],
      filters: [],
      searchColumns: [],
      limitations: [],
    }),
    [dataset, drill.label],
  );

  const filterKey = JSON.stringify(drill.filters);
  const { data, failure, busy } = useAsync(
    () => api.renderDashboard(dataset.id, spec, drill.filters, ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataset.id, filterKey],
  );

  const block = data?.dashboard.blocks[0];
  const table = block?.data?.kind === 'table' ? block.data : null;
  const typeOf = new Map(dataset.columns.map((c) => [c.name, c.type]));

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <header>
        <h2>Records: {drill.label}</h2>
        <button className="ghost tiny" onClick={onClose}>
          Close
        </button>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}
      {busy && table === null && (
        <p className="muted">
          <span className="spinner" /> Fetching the rows…
        </p>
      )}

      {block !== undefined && table === null && failure === null && (
        <p className="muted">{block.unavailable ?? 'No rows match this selection.'}</p>
      )}

      {table !== null && data !== null && (
        <>
          <p className="muted" style={{ marginTop: 0 }}>
            {data.dashboard.rowCount.toLocaleString('en-US')} row(s) match.
            {table.totalRows > table.rows.length &&
              ` Showing the first ${table.rows.length.toLocaleString('en-US')}.`}
          </p>
          <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table>
              <thead>
                <tr>
                  {table.columns.map((name) => (
                    <th key={name} className={typeOf.get(name) === 'numeric' ? 'num' : undefined}>
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, index) => (
                  <tr key={index}>
                    {row.map((value, cellIndex) => {
                      const name = table.columns[cellIndex] ?? '';
                      const missing = value === null || value === '';
                      return (
                        <td
                          key={cellIndex}
                          className={[
                            typeOf.get(name) === 'numeric' ? 'num' : '',
                            missing ? 'empty' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {missing ? '—' : String(value)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Explore — the ad-hoc query builder
// -----------------------------------------------------------------------------

export function ExploreTab({ dataset, colors }: { dataset: Dataset; colors: VizColors }) {
  const dims = dimensions(dataset.columns);
  const dates = temporals(dataset.columns);
  const meas = measures(dataset.columns);

  const [groupBy, setGroupBy] = useState(() => dims[0]?.name ?? dates[0]?.name ?? '');
  const [measure, setMeasure] = useState(() => meas[0]?.name ?? '');
  const [aggregation, setAggregation] = useState<Aggregation>('sum');
  const [period, setPeriod] = useState<CalendarPeriod>('month');
  const [view, setView] = useState<ChartKind | null>(null);

  const isTemporal = dates.some((c) => c.name === groupBy);
  const needsMeasure = aggregation !== 'count';

  const [drill, setDrill] = useState<DrillSelection | null>(null);

  const { data, failure, busy } = useAsync(
    () =>
      api.query(dataset.id, {
        groupBy: groupBy === '' ? undefined : groupBy,
        measure: needsMeasure && measure !== '' ? measure : undefined,
        aggregation,
        limit: 20,
        sort: isTemporal ? 'keyAsc' : 'valueDesc',
        ...(isTemporal ? { period } : {}),
      }),
    [dataset.id, groupBy, measure, aggregation, period, isTemporal, needsMeasure],
  );

  const label = needsMeasure ? `${aggregation}(${measure})` : 'rows';

  // The default follows the data — a time series is a line, a dimension is bars —
  // and an explicit pick overrides it until the user clears it.
  const shown: ChartKind = view ?? (isTemporal ? 'line' : 'bar');
  const VIEWS: { id: ChartKind; label: string }[] = [
    { id: 'bar', label: 'Bar' },
    { id: 'line', label: 'Line' },
    { id: 'area', label: 'Area' },
    { id: 'pie', label: 'Share' },
    { id: 'table', label: 'Table' },
  ];

  return (
    <>
    <div className="card">
      <header>
        <h2>Explore</h2>
        <span className="sub">
          {data === null ? '' : `${data.matchedRows.toLocaleString('en-US')} rows`}
        </span>
      </header>

      <div className="row" style={{ marginBottom: 14 }}>
        <label className="field">
          Group by
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            {[...dims, ...dates].map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        {isTemporal && (
          <label className="field">
            Period
            <select value={period} onChange={(e) => setPeriod(e.target.value as CalendarPeriod)}>
              {PERIODS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="field">
          Aggregation
          <select value={aggregation} onChange={(e) => setAggregation(e.target.value as Aggregation)}>
            {AGGREGATIONS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>

        {needsMeasure && (
          <label className="field">
            Measure
            <select value={measure} onChange={(e) => setMeasure(e.target.value)}>
              {meas.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}
      {busy && data === null && <p className="muted">Running…</p>}

      {data !== null && (
        <>
          <div className="segmented" style={{ marginBottom: 12 }}>
            {VIEWS.map((option) => (
              <button
                key={option.id}
                aria-pressed={shown === option.id}
                onClick={() => setView(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {shown === 'line' && (
            <TrendChart buckets={data.result.buckets} measureLabel={label} colors={colors} />
          )}
          {shown === 'area' && (
            <TrendChart buckets={data.result.buckets} measureLabel={label} colors={colors} area />
          )}
          {shown === 'pie' && <ShareChart buckets={data.result.buckets} colors={colors} />}
          {shown === 'bar' && (
            <CategoryChart
              buckets={data.result.buckets}
              otherBucket={data.result.otherBucket}
              measureLabel={label}
              colors={colors}
            />
          )}
          {shown === 'scatter' && (
            <ScatterPlot
              points={data.result.buckets.map((b, i) => ({ x: i, y: b.value }))}
              xLabel="index"
              yLabel={label}
              colors={colors}
            />
          )}
          {shown === 'table' && (
            <>
              <p className="muted" style={{ marginTop: 0 }}>
                Click a row to see the records behind it.
              </p>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{groupBy}</th>
                      <th className="num">{label}</th>
                      <th className="num">Rows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.result.buckets.map((bucket) => (
                      <tr
                        key={bucket.key}
                        style={{ cursor: 'pointer' }}
                        title="View these records"
                        onClick={() => {
                          const filters = drillFilters(groupBy, isTemporal, period, bucket.key);
                          if (filters.length === 0) return;
                          setDrill({ label: `${groupBy} = ${bucket.key}`, filters });
                        }}
                      >
                        <td>{bucket.key}</td>
                        <td className="num">{num(bucket.value)}</td>
                        <td className="num">{bucket.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {data !== null && data.result.otherBucket !== null && (
        <p className="muted" style={{ marginTop: 8 }}>
          Showing the top {data.result.buckets.length} of {data.result.totalGroups} groups; the rest
          are folded into “{data.result.otherBucket.key}”.
        </p>
      )}
    </div>

    {drill !== null && (
      <RecordsPanel dataset={dataset} drill={drill} onClose={() => setDrill(null)} />
    )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Data — the raw table
// -----------------------------------------------------------------------------

export function DataTab({ dataset }: { dataset: Dataset }) {
  const [offset, setOffset] = useState(0);
  const limit = 100;

  const { data, failure, busy } = useAsync(
    () => api.rows(dataset.id, offset, limit),
    [dataset.id, offset],
  );

  const typeOf = useMemo(
    () => new Map(dataset.columns.map((c) => [c.name, c.type])),
    [dataset.columns],
  );

  return (
    <div className="card">
      <header>
        <h2>{dataset.fileName}</h2>
        <span className="sub">
          {dataset.rowCount.toLocaleString('en-US')} rows · {dataset.columns.length} columns
        </span>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      <div className="row" style={{ marginBottom: 12 }}>
        {dataset.columns.map((column) => (
          <span className="badge" key={column.name} title={`${column.distinct} distinct`}>
            {column.name}
            <span style={{ color: 'var(--ink-muted)', fontWeight: 400 }}>{column.type}</span>
            {column.missing > 0 && (
              <span style={{ color: 'var(--ink-muted)', fontWeight: 400 }}>
                · {column.missing} missing
              </span>
            )}
          </span>
        ))}
      </div>

      <div className="table-wrap" style={{ maxHeight: 520, overflowY: 'auto' }}>
        <table>
          <thead>
            <tr>
              {(data?.columns ?? dataset.columns.map((c) => c.name)).map((name) => (
                <th key={name} className={typeOf.get(name) === 'numeric' ? 'num' : undefined}>
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data?.rows ?? []).map((row, index) => (
              <tr key={offset + index}>
                {(data?.columns ?? []).map((name) => {
                  const value = row[name];
                  const missing = value === null || value === undefined || value === '';
                  return (
                    <td
                      key={name}
                      className={[
                        typeOf.get(name) === 'numeric' ? 'num' : '',
                        missing ? 'empty' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {/* An empty cell is marked as such rather than rendered
                          as blank space that could be mistaken for a value. */}
                      {missing ? '—' : String(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        <button onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0 || busy}>
          Previous
        </button>
        <button
          onClick={() => setOffset(offset + limit)}
          disabled={busy || offset + limit >= (data?.total ?? 0)}
        >
          Next
        </button>
        <span className="muted">
          Rows {offset + 1}–{Math.min(offset + limit, data?.total ?? 0)} of{' '}
          {(data?.total ?? 0).toLocaleString('en-US')}
        </span>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Pivot
// -----------------------------------------------------------------------------

export function PivotTab({ dataset }: { dataset: Dataset }) {
  const dims = dimensions(dataset.columns);
  const meas = measures(dataset.columns);

  const [rowField, setRowField] = useState(() => dims[0]?.name ?? '');
  const [columnField, setColumnField] = useState(() => dims[1]?.name ?? '');
  const [measure, setMeasure] = useState(() => meas[0]?.name ?? '');
  const [aggregation, setAggregation] = useState<Aggregation>('sum');
  const [drill, setDrill] = useState<DrillSelection | null>(null);

  const needsMeasure = aggregation !== 'count';

  const { data, failure, busy } = useAsync(
    () =>
      api.pivot(dataset.id, {
        rowFields: [rowField],
        columnField: columnField === '' ? undefined : columnField,
        measure: needsMeasure && measure !== '' ? measure : undefined,
        aggregation,
        rowLimit: 50,
        columnLimit: 20,
      }),
    [dataset.id, rowField, columnField, measure, aggregation, needsMeasure],
  );

  if (dims.length === 0) {
    return (
      <div className="card">
        <header>
          <h2>Pivot</h2>
        </header>
        <p className="muted">
          This file has no categorical columns to pivot on. A pivot needs at least one dimension.
        </p>
      </div>
    );
  }

  return (
    <>
    <div className="card">
      <header>
        <h2>Pivot</h2>
        <span className="sub">{data === null ? '' : `${data.matchedRows.toLocaleString('en-US')} rows`}</span>
      </header>

      <div className="row" style={{ marginBottom: 14 }}>
        <label className="field">
          Rows
          <select value={rowField} onChange={(e) => setRowField(e.target.value)}>
            {dims.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          Columns
          <select value={columnField} onChange={(e) => setColumnField(e.target.value)}>
            <option value="">(none)</option>
            {dims
              .filter((c) => c.name !== rowField)
              .map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
          </select>
        </label>

        <label className="field">
          Aggregation
          <select value={aggregation} onChange={(e) => setAggregation(e.target.value as Aggregation)}>
            {AGGREGATIONS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>

        {needsMeasure && (
          <label className="field">
            Measure
            <select value={measure} onChange={(e) => setMeasure(e.target.value)}>
              {meas.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}
      {busy && data === null && <p className="muted">Building…</p>}
      {data !== null && (
        <PivotGrid
          pivot={data.pivot}
          onDrill={(keys) => {
            const filters: Filter[] = [];
            const parts: string[] = [];
            if (keys.row !== null) {
              filters.push(cellFilter(rowField, keys.row));
              parts.push(`${rowField} = ${keys.row}`);
            }
            if (keys.column !== null && columnField !== '') {
              filters.push(cellFilter(columnField, keys.column));
              parts.push(`${columnField} = ${keys.column}`);
            }
            setDrill({ label: parts.length === 0 ? 'all rows' : parts.join(' · '), filters });
          }}
        />
      )}
    </div>

    {drill !== null && (
      <RecordsPanel dataset={dataset} drill={drill} onClose={() => setDrill(null)} />
    )}
    </>
  );
}

function PivotGrid({
  pivot,
  onDrill,
}: {
  pivot: PivotTable;
  /** Called with the bucket keys a cell aggregates over; null means "all". */
  onDrill?: (keys: { row: string | null; column: string | null }) => void;
}) {
  // "Total" is only honest for additive aggregations. A row of averages summed
  // is a number that measures nothing, so the margin is labelled for what it is.
  const totalLabel = pivot.totalsAreAdditive ? 'Total' : `Overall`;

  const drillable = onDrill !== undefined;
  const cellProps = (keys: { row: string | null; column: string | null }, empty: boolean) =>
    drillable && !empty
      ? {
          style: { cursor: 'pointer' },
          title: 'View these records',
          onClick: () => onDrill(keys),
        }
      : {};

  return (
    <>
      {drillable && (
        <p className="muted" style={{ marginTop: 0 }}>
          Click a figure to see the records behind it.
        </p>
      )}
      <div className="table-wrap" style={{ maxHeight: 520, overflowY: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>{pivot.rowFields.join(' · ')}</th>
              {pivot.columnKeys.map((key) => (
                <th key={key} className="num">
                  {key}
                </th>
              ))}
              <th className="num">{totalLabel}</th>
            </tr>
          </thead>
          <tbody>
            {pivot.rows.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                {row.cells.map((cell, index) => (
                  <td
                    key={index}
                    className={cell.value === null ? 'num empty' : 'num'}
                    {...cellProps(
                      { row: row.keys[0] ?? row.label, column: pivot.columnKeys[index] ?? null },
                      cell.value === null,
                    )}
                  >
                    {/* An empty intersection shows a dash, never 0 — "no orders"
                        and "orders worth zero" are different facts. */}
                    {cell.value === null ? '—' : num(cell.value)}
                  </td>
                ))}
                <td className="num" {...cellProps({ row: row.keys[0] ?? row.label, column: null }, row.total.value === null)}>
                  {num(row.total.value)}
                </td>
              </tr>
            ))}
            <tr className="total">
              <td>{totalLabel}</td>
              {pivot.columnTotals.map((cell, index) => (
                <td
                  key={index}
                  className="num"
                  {...cellProps(
                    { row: null, column: pivot.columnKeys[index] ?? null },
                    cell.value === null,
                  )}
                >
                  {num(cell.value)}
                </td>
              ))}
              <td className="num" {...cellProps({ row: null, column: null }, pivot.grandTotal.value === null)}>
                {num(pivot.grandTotal.value)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="muted" style={{ marginTop: 8 }}>
        {!pivot.totalsAreAdditive && (
          <>
            Margins are the aggregation over all underlying values, not the sum of the cells.{' '}
          </>
        )}
        {pivot.truncatedRows > 0 && <>{pivot.truncatedRows} further rows not shown. </>}
        {pivot.truncatedColumns > 0 && (
          <>
            {pivot.truncatedColumns} further columns not shown — they are still included in the row
            margins.
          </>
        )}
      </p>
    </>
  );
}

// -----------------------------------------------------------------------------
// Reports
// -----------------------------------------------------------------------------

export function ReportsTab({ onOpen }: { onOpen: (report: Report) => void }) {
  const { data, failure, busy, reload } = useAsync(() => api.listReports(), []);

  return (
    <div className="card">
      <header>
        <h2>Saved reports</h2>
        <button className="ghost" onClick={reload} disabled={busy}>
          Refresh
        </button>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {data !== null && data.reports.length === 0 && (
        <p className="muted">
          No reports yet. Every upload saves one automatically.
        </p>
      )}

      {data !== null && data.reports.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Report</th>
                <th className="num">Findings</th>
                <th>Summary</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.reports.map((report) => (
                <tr key={report.id}>
                  <td>{report.title}</td>
                  <td className="num">{report.insights.length}</td>
                  <td>
                    {report.narrative === null ? (
                      <span className="muted">not written</span>
                    ) : report.narrative.degraded ? (
                      <span className="badge warning">offline</span>
                    ) : (
                      <span className="badge">{report.narrative.provider}</span>
                    )}
                  </td>
                  <td>{new Date(report.createdAt).toLocaleString()}</td>
                  <td>
                    <div className="row">
                      <button onClick={() => onOpen(report)}>Open</button>
                      <a href={api.exportUrl(report.id, 'pdf')} download>
                        <button className="ghost">PDF</button>
                      </a>
                      <button
                        className="ghost"
                        onClick={async () => {
                          await api.deleteReport(report.id);
                          reload();
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
